import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

/**
 * "Default time period" control for the Share price chart section (RL-2448).
 * Admins choose which range the chart opens on; the chart still lets visitors
 * switch to any other range once it has loaded.
 *
 * Values match the API (`price_chart_default_period`). The API returns null for
 * a business that has never chosen one, and every chart keeps its existing
 * default in that case — see `resolveChartPeriod` in `@/src/components/TradingWidget`.
 */
export const DEFAULT_CHART_PERIOD = '1Y'

/**
 * Tenants whose chart opened on something other than 1Y before this setting
 * existed. They keep that range until an admin picks one, so turning the
 * setting on does not quietly re-range a live homepage.
 */
export const LEGACY_CHART_PERIODS: Record<string, ChartPeriod> = { AMU: '3M' }

export const CHART_PERIOD_OPTIONS = [
  { value: '1D', label: '1D' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '1Y', label: '1Y' },
  { value: 'ALL', label: 'All' }
] as const

export type ChartPeriod = (typeof CHART_PERIOD_OPTIONS)[number]['value']

const isChartPeriod = (value: unknown): value is ChartPeriod =>
  CHART_PERIOD_OPTIONS.some(option => option.value === value)

/**
 * The period a chart should actually open on: what the admin saved, or — when
 * nothing is saved — whatever that tenant's chart has always shown.
 *
 * Shared by the settings control and the chart itself so the preselected
 * option is never a different range from what the homepage renders.
 */
export const resolveChartPeriod = (saved: unknown, tenantCode?: string): ChartPeriod => {
  const normalised = typeof saved === 'string' ? saved.trim().toUpperCase() : saved
  if (isChartPeriod(normalised)) return normalised
  return LEGACY_CHART_PERIODS[String(tenantCode || '').toUpperCase()] ?? DEFAULT_CHART_PERIOD
}

interface ChartPeriodFieldProps {
  value: ChartPeriod
  onChange: (value: ChartPeriod) => void
  label?: string
  caption?: string
}

const ChartPeriodField = ({
  value,
  onChange,
  label = 'Default time period',
  caption = 'The share price chart will open on this period. Visitors can still switch to any other period.'
}: ChartPeriodFieldProps) => (
  <Box className="flex flex-col gap-1">
    <Typography color="text.primary" variant="subtitle2">
      {label}
    </Typography>
    {caption && (
      <Typography color="text.secondary" variant="caption">
        {caption}
      </Typography>
    )}
    <ToggleButtonGroup
      exclusive
      size="small"
      value={value}
      // `null` arrives when the selected button is clicked again; keep the
      // current period rather than leaving the chart without a default.
      onChange={(_, next) => isChartPeriod(next) && onChange(next)}
      aria-label="Default time period"
      className="mt-1 w-fit"
    >
      {CHART_PERIOD_OPTIONS.map(option => (
        <ToggleButton
          key={option.value}
          value={option.value}
          aria-label={option.label}
          className="px-4 normal-case"
        >
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  </Box>
)

export default ChartPeriodField
